import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useProposalProgress } from '../hooks/useProposalProgress';
const ProposalProgressContext = /*#__PURE__*/ createContext(undefined);
/**
 * Provider for proposal document-analysis progress.
 *
 * Mounted in DashboardLayout so the bar can render at the breadcrumb level
 * on every `/proposal/:id/*` route. The hook is gated to proposal routes
 * specifically — on `/project/:projectId/*` the underlying useProposalProject
 * throws because the project type isn't 'proposal', so we skip the work
 * entirely rather than swallowing an error per navigation.
 *
 * React Query dedupes the underlying batchStatus query by key, so the bar
 * shares a single 2-second poll with the existing BatchProcessingProgress.
 */ export function ProposalProgressProvider({ children }) {
    const params = useParams();
    const location = useLocation();
    const isProposalRoute = location.pathname.startsWith('/proposal/');
    const projectId = isProposalRoute ? params.id ?? params.projectId ?? null : null;
    // `useProposalProgress` memoises its return on the underlying primitives,
    // so the result reference is stable across renders when nothing changed.
    // No extra useMemo needed here.
    const value = useProposalProgress(projectId);
    return /*#__PURE__*/ _jsx(ProposalProgressContext.Provider, {
        value: value,
        children: children
    });
}
/**
 * Read proposal progress state. Returns null-ish values when called outside a proposal route.
 */ export function useProposalProgressContext() {
    const ctx = useContext(ProposalProgressContext);
    if (!ctx) {
        return {
            projectId: null,
            batchId: null,
            snapshot: null,
            isLoading: false,
            error: null
        };
    }
    return ctx;
}
